import { Gift, ImageOff } from "lucide-react";
import { Order } from "../../SalesPage/SalesPageInterface";
import { ProductInterface, ProductVariant } from "../OrderDetailsInterface";
import { dictionary } from "../../../utils/dictionary";
import { useAuth } from "../../../context/AuthContext";
import { getOrderTotals } from "./orderTotals";

const variantAttributesText = (variant: ProductVariant) => {
  const attributes = Array.isArray(variant.attributes) ? variant.attributes : [];

  if (attributes.length === 0) {
    return variant.name || variant.variant_key || "Variante";
  }

  return attributes
    .map((attribute) => {
      const displayValue =
        "color" in attribute && attribute.color
          ? attribute.color.name
          : "value" in attribute
            ? attribute.value
            : "";
      return `${attribute.name}: ${displayValue}`;
    })
    .filter(Boolean)
    .join(" · ");
};

export const OrderProductsCard = ({
  order,
  products,
  className = "",
}: {
  order: Order;
  products: ProductInterface[];
  className?: string;
}) => {
  const { language } = useAuth();
  const { total, currencyCode, formatAmount } = getOrderTotals(order, products);
  const benefits = order.benefit_purchases ?? [];
  const totalUnits =
    order.total_units || products.reduce((sum, product) => sum + (product.units || 1), 0);

  return (
    <div
      className={`flex flex-col rounded-2xl border border-gray-100 bg-white p-5 shadow-[0_2px_12px_rgba(112,144,176,0.08)] ${className}`}
    >
      <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
        {dictionary["order-products"][language]}
        <span className="ml-1 normal-case tracking-normal text-gray-300">
          · {totalUnits} {dictionary["units-label"][language]}
        </span>
      </p>

      <div className="mt-1 divide-y divide-gray-100">
        {products.map((product) => {
          const variants = product.product_variants ?? [];
          const unitPrice = product.unit_price_with_discount;
          // Mismo criterio que el detalle anterior: si alguna variante trae un
          // precio propio distinto al unitario, la línea "unitario × unidades"
          // sería engañosa y cada variante muestra su precio.
          const hasVariantPricing = variants.some(
            (variant) => Number(variant.price) > 0 && Number(variant.price) !== unitPrice
          );

          return (
            <div key={product.id} className="flex items-start gap-3 py-3.5">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-gray-100">
                {product.images && product.images.length > 0 ? (
                  <img
                    src={product.images[0]}
                    alt={product.name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <ImageOff className="h-4 w-4 text-gray-300" />
                )}
              </div>

              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium leading-5 text-gray-900">{product.name}</p>
                {variants.map((variant) => {
                  const variantPrice =
                    Number(variant.price) > 0 ? Number(variant.price) : unitPrice;
                  return (
                    <p key={variant.id} className="mt-0.5 text-xs leading-4 text-gray-500">
                      {variantAttributesText(variant)}
                      {variant.units > 1 && <span className="text-gray-400"> ×{variant.units}</span>}
                      {hasVariantPricing && (
                        <span className="text-gray-400"> · {formatAmount(variantPrice)} c/u</span>
                      )}
                    </p>
                  );
                })}
                <p className="mt-0.5 text-[11px] leading-4 text-gray-400">
                  SKU {product.product_id}
                </p>
              </div>

              <div className="shrink-0 text-right">
                {!hasVariantPricing && (
                  <p className="text-xs leading-4 text-gray-500 tabular-nums">
                    {formatAmount(unitPrice)} × {product.units || 1}
                  </p>
                )}
                <p className="mt-0.5 text-sm font-semibold leading-5 text-gray-900 tabular-nums">
                  {formatAmount(product.total_value_with_discount)}
                </p>
              </div>
            </div>
          );
        })}

        {benefits.map((benefit, index) => (
          <div key={`benefit-${index}`} className="flex items-center justify-between gap-3 py-2.5">
            <p className="flex min-w-0 items-center gap-1.5 text-xs font-medium text-violet-700">
              <Gift className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">
                {benefit.name || benefit.label || dictionary["additional-offer"][language]}
              </span>
            </p>
            <p className="shrink-0 text-sm font-semibold text-gray-900 tabular-nums">
              {benefit.price > 0 ? formatAmount(benefit.price) : dictionary["free"][language]}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-auto flex items-baseline justify-between border-t border-gray-100 pt-3">
        <span className="text-xs text-gray-500">
          {totalUnits} {dictionary["units-label"][language]}
        </span>
        <span className="text-sm font-semibold text-gray-900 tabular-nums">
          {dictionary["total"][language]} {formatAmount(total)}{" "}
          <span className="text-xs font-normal text-gray-400">{currencyCode}</span>
        </span>
      </div>
    </div>
  );
};
